// Orkestrerat save-flöde: serialIndex + foton + DB-save i en samlad sekvens.
// Denna fil skapar en ny kodväg utan att röra befintliga funktioner än.

import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import {
    deleteField,
    doc,
    runTransaction,
    serverTimestamp,
} from "firebase/firestore";
import {
    deleteObject,
    getDownloadURL,
    ref as storageRef,
    uploadBytes,
    type FirebaseStorage,
} from "firebase/storage";
import type { Firestore } from "firebase/firestore";
import { saveEditAtomicallyWithSerial } from "./editModalFunctions";
import type { EditFormState } from "./EditModal";

type SerialAlloc = {
    changed: boolean;
    serialBaseKey: string;
    serialVisit: number;
    displaySerial: string;
};

export type StableSaveArgs = {
    db: Firestore;
    storage: FirebaseStorage;
    editId: string | null;
    editForm: EditFormState;
    editIsReadOnly: boolean;

    // Serial- och låshjälpare
    currentUserString: () => string | null;
    toMillis: (v: unknown) => number | null;
    LOCK_STALE_MS: number;
    splitSerialParts: (s: string) => { base: string; visit: number };
    normalizeSerialKey: (s: string) => string;
    buildDisplaySerial: (base: string, visit: number) => string;
    originalSerial?: string | null;
    originalSerialBaseKey?: string | null;
    lastSerialAllocRef: MutableRefObject<SerialAlloc | null>;
    editOriginalRef: MutableRefObject<{ serial: string; serialBaseKey: string | null; itemId: string | null }>;

    // Foton (staged uploads/deletes)
    pendingPhotoFiles: Record<string, File>;
    pendingDeletes: Record<string, true>;
    previewUrls: Record<string, string>;
    setPreviewUrls: Dispatch<SetStateAction<Record<string, string>>>;
    setPendingPhotoFiles: Dispatch<SetStateAction<Record<string, File>>>;
    setPendingDeletes: Dispatch<SetStateAction<Record<string, true>>>;

    // UI state
    setEditForm: Dispatch<SetStateAction<EditFormState>>;
    setIsSaving: (v: boolean) => void;
    refreshHomeList: () => Promise<void>;
    setIsEditOpen: (v: boolean) => void;
    setEditId: (v: string | null) => void;

    alertFn?: (msg: string) => void;
    consoleError?: (e: unknown) => void;
};

type PartialItem = {
    invoiceReportId?: unknown;
    completed?: unknown;
    lockedBy?: string | null;
    lockedAt?: unknown;
};

async function commitStagedPhotos(args: StableSaveArgs): Promise<void> {
    const {
        db, storage, editId, editForm,
        pendingPhotoFiles, pendingDeletes, previewUrls,
        setPreviewUrls, setPendingPhotoFiles, setPendingDeletes, setEditForm,
        toMillis, LOCK_STALE_MS, currentUserString,
    } = args;
    if (!editId) return;

    const adds = Object.entries(pendingPhotoFiles);
    const dels = Object.keys(pendingDeletes);
    if (adds.length === 0 && dels.length === 0) return;

    const itemRef = doc(db, "itInventory", editId);

    const prevUrls: Record<string, string | undefined> = {};
    const photos = editForm?.photos as Record<string, string | undefined> | undefined;
    for (const [type] of adds) prevUrls[type] = photos?.[type];
    for (const type of dels) prevUrls[type] = photos?.[type];

    const urlPatch: Record<string, string> = {};
    for (const [type, file] of adds) {
        const blob = file;
        const path = `photos/${editId}/${type}.jpg`;
        const sref = storageRef(storage, path);
        await uploadBytes(sref, blob, { contentType: file.type || "image/jpeg" });
        const url = await getDownloadURL(sref);
        urlPatch[type] = url;

        const prevUrl = prevUrls[type];
        if (prevUrl) {
            try {
                const prevPath = decodeURIComponent(new URL(prevUrl).pathname.split("/o/")[1].split("?")[0]);
                if (prevPath && prevPath !== path) {
                    await deleteObject(storageRef(storage, prevPath));
                }
            } catch { /* ignore */ }
        }
    }

    const updates: Record<string, unknown> = { updatedAt: serverTimestamp() };
    for (const [type, url] of Object.entries(urlPatch)) updates[`photos.${type}`] = url;
    for (const type of dels) updates[`photos.${type}`] = deleteField();

    if (Object.keys(updates).length > 1) {
        await runTransaction(db, async (tx) => {
            const snap = await tx.get(itemRef);
            if (!snap.exists()) throw new Error("Posten finns inte längre.");
            const cur = snap.data() as PartialItem;

            if (cur.invoiceReportId) throw new Error("Posten är fakturerad och kan inte ändras.");
            if (cur.completed) throw new Error("Posten är markerad som färdig och kan inte ändras.");

            const me = currentUserString();
            const heldByOther = !!cur.lockedBy && cur.lockedBy !== me;
            const lockedAtMs = toMillis(cur.lockedAt);
            const isStale = lockedAtMs === null ? true : (Date.now() - lockedAtMs) > LOCK_STALE_MS;
            if (heldByOther && !isStale) throw new Error(`Posten redigeras av ${cur.lockedBy}.`);

            tx.update(itemRef, updates);
        });
    }

    for (const type of dels) {
        const prevUrl = prevUrls[type];
        let deletedViaPrev = false;

        if (prevUrl) {
            try {
                const prevPath = decodeURIComponent(
                    new URL(prevUrl).pathname.split("/o/")[1].split("?")[0]
                );
                if (prevPath) {
                    await deleteObject(storageRef(storage, prevPath));
                    deletedViaPrev = true;
                }
            } catch { /* best-effort */ }
        }

        if (!deletedViaPrev) {
            try {
                await deleteObject(storageRef(storage, `photos/${editId}/${type}.jpg`));
            } catch { /* best-effort */ }
        }
    }

    setEditForm((prev) => {
        const nextPhotos = { ...(prev.photos || {}) } as Record<string, string>;
        for (const [type, url] of Object.entries(urlPatch)) nextPhotos[type] = url;
        for (const type of dels) delete nextPhotos[type];
        return { ...prev, photos: nextPhotos };
    });

    for (const [type] of adds) {
        const u = previewUrls[type];
        if (u?.startsWith?.("blob:")) { try { URL.revokeObjectURL(u); } catch { /* ignore */ } }
    }
    setPreviewUrls({});
    setPendingPhotoFiles({});
    setPendingDeletes({});
}

// Samlad save-sekvens: reallocate serial, commit staged photos, och skriv fälten.
export async function stableSaveOrchestrated(args: StableSaveArgs): Promise<void> {
    const {
        db,
        editId,
        editForm,
        editIsReadOnly,
        currentUserString,
        toMillis,
        LOCK_STALE_MS,
        splitSerialParts,
        normalizeSerialKey,
        buildDisplaySerial,
        originalSerial,
        originalSerialBaseKey,
        lastSerialAllocRef,
        editOriginalRef,
        refreshHomeList,
        setIsEditOpen,
        setEditId,
        setIsSaving,
        setEditForm,
        alertFn = (m) => alert(m),
        consoleError = console.error,
    } = args;

    if (!editId) return;

    setIsSaving(true);
    try {
        const nextSerial = String(editForm.serial ?? "").trim();
        const nextSerialParts = splitSerialParts(nextSerial);
        const nextBaseKey = normalizeSerialKey(nextSerialParts.base);
        const originalBaseKey = originalSerialBaseKey
            ?? (originalSerial ? normalizeSerialKey(splitSerialParts(originalSerial).base) : null);
        const originalVisit = originalSerial
            ? splitSerialParts(originalSerial).visit
            : null;
        const serialChanged = !!nextBaseKey && !!originalBaseKey && (
            nextBaseKey !== originalBaseKey
            || (originalVisit !== null && nextSerialParts.visit !== originalVisit)
        );
        const hasPendingPhotos = Object.keys(args.pendingPhotoFiles).length > 0
            || Object.keys(args.pendingDeletes).length > 0;

        if (serialChanged && hasPendingPhotos) {
            alertFn("Serienummer och bilder kan inte ändras i samma sparning. Spara serienummer och övriga uppgifter först och ändra därefter bilderna separat.");
            return;
        }

        await commitStagedPhotos(args);

        const alloc = await saveEditAtomicallyWithSerial({
            db,
            editId,
            editForm,
            editIsReadOnly,
            currentUserString,
            toMillis,
            LOCK_STALE_MS,
            splitSerialParts,
            normalizeSerialKey,
            buildDisplaySerial,
            refreshHomeList,
            setIsEditOpen,
            setEditId,
            setEditForm,
            alertFn,
            consoleError,
        });
        lastSerialAllocRef.current = alloc;
        editOriginalRef.current = {
            serial: alloc.displaySerial,
            serialBaseKey: alloc.serialBaseKey,
            itemId: editId,
        };
    } catch (err: unknown) {
        consoleError(err);
        const msg = err instanceof Error ? err.message : String(err);
        alertFn("Kunde inte spara ändringar: " + msg);
    } finally {
        setIsSaving(false);
    }
}

// I fallback-scenarion kan vi behöva köra foto-commit separat (t.ex. om saveEdit inte
// ska köras). Exporteras för enkel testning i nästa steg av refaktorn.
export async function commitPhotosOnly(args: StableSaveArgs): Promise<void> {
    await commitStagedPhotos(args);
}
